import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  EmptyState,
  Spinner,
  useConfirm,
  useToast,
} from "../../../design-system";
import {
  archiveDocument,
  deleteDocument,
  downloadDocument,
  getDocumentCategories,
  getDocuments,
  publishDocument,
  unarchiveDocument,
} from "../../../helpers/documents_helper";
import { unwrap } from "../../../pages/Documentos/helpers";
import { DocumentsTable } from "../../../pages/Documentos/components/DocumentsTable";
import { DocumentDetail } from "../../../pages/Documentos/components/DocumentDetail";
import { UploadDocumentDrawer } from "../../../pages/Documentos/components/UploadDocumentDrawer";
import { useDocumentDetail } from "../../../pages/Documentos/useDocumentDetail";

/**
 * Sub-pestaña "Documentos" del drawer de empleado. Lista los documentos del
 * empleado y permite a un manager subir uno nuevo pre-asociado.
 */
export default function EmployeeDocumentsTab({ empleado }) {
  const toast = useToast();
  const confirm = useConfirm();
  const employeeProfileId = empleado?.id || null;

  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const { events, eventsLoading, previewUrl, previewLoading, previewError } =
    useDocumentDetail(openDoc);

  const load = useCallback(async () => {
    if (!employeeProfileId) return;
    setLoading(true);
    setError(null);
    try {
      const [docsRes, catRes] = await Promise.all([
        getDocuments({ employee_profile_id: employeeProfileId, include_archived: 1 }),
        getDocumentCategories().catch(() => ({ data: [] })),
      ]);
      setDocs(unwrap(docsRes) || []);
      setCategories(unwrap(catRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los documentos.");
    } finally {
      setLoading(false);
    }
  }, [employeeProfileId]);

  useEffect(() => {
    load();
  }, [load]);

  const onDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.original_filename || doc.title);
    } catch (e) {
      toast.error("No se pudo descargar", typeof e === "string" ? e : e?.message);
    }
  };

  const onPublish = async (doc) => {
    try {
      await publishDocument(doc.id);
      toast.success("Documento publicado");
      load();
    } catch (e) {
      toast.error("No se pudo publicar", typeof e === "string" ? e : e?.message);
    }
  };

  const onArchive = async (doc) => {
    const ok = await confirm({
      title: "Archivar documento",
      description: `Archivar "${doc.title}".`,
      confirmLabel: "Archivar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await archiveDocument(doc.id);
      toast.success("Documento archivado");
      setOpenDoc(null);
      load();
    } catch (e) {
      toast.error("No se pudo archivar", typeof e === "string" ? e : e?.message);
    }
  };

  const onUnarchive = async (doc) => {
    try {
      const res = await unarchiveDocument(doc.id);
      const updated = unwrap(res);
      toast.success("Documento desarchivado");
      setOpenDoc(updated || null);
      load();
    } catch (e) {
      toast.error("No se pudo desarchivar", typeof e === "string" ? e : e?.message);
    }
  };

  const onDelete = async (doc) => {
    const ok = await confirm({
      title: "Eliminar documento",
      description: `Eliminar "${doc.title}". El archivo se borrará del almacenamiento.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteDocument(doc.id);
      toast.success("Documento eliminado");
      setOpenDoc(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  // Lista mínima de empleados para el picker del drawer de subida.
  const employees = empleado
    ? [
        {
          id: empleado.id,
          first_name: empleado.first_name || empleado.name?.split(" ")?.[0] || empleado.name || "",
          last_name: empleado.last_name || empleado.name?.split(" ")?.slice(1).join(" ") || "",
          email: empleado.email,
        },
      ]
    : [];

  return (
    <Card padded={false}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid var(--kr-border-subtle)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            Documentos del empleado
          </div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            {docs.length} {docs.length === 1 ? "documento" : "documentos"}
          </div>
        </div>
        <Button variant="primary" icon="Upload" size="sm" onClick={() => setShowUpload(true)}>
          Subir documento
        </Button>
      </div>

      {error && (
        <div style={{ padding: 12 }}>
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 32, textAlign: "center" }}>
          <Spinner size={16} />
        </div>
      ) : docs.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Sin documentos"
          description="Sube el primer documento del empleado desde el botón superior."
        />
      ) : (
        <DocumentsTable
          rows={docs}
          employeesById={{}}
          isManager
          hideEmployeeColumn
          onOpen={setOpenDoc}
          onDownload={onDownload}
        />
      )}

      <Drawer
        open={!!openDoc}
        onClose={() => setOpenDoc(null)}
        size="lg"
        title={openDoc?.title || "Documento"}
        footer={
          openDoc ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button variant="ghost" icon="Download" onClick={() => onDownload(openDoc)}>
                Descargar
              </Button>
              <div style={{ display: "flex", gap: 8 }}>
                {openDoc.status === "draft" && (
                  <Button variant="primary" icon="Check" onClick={() => onPublish(openDoc)}>
                    Publicar
                  </Button>
                )}
                {openDoc.status === "archived" ? (
                  <Button variant="secondary" icon="ArchiveRestore" onClick={() => onUnarchive(openDoc)}>
                    Desarchivar
                  </Button>
                ) : (
                  <Button variant="dangerSoft" icon="Archive" onClick={() => onArchive(openDoc)}>
                    Archivar
                  </Button>
                )}
                <Button variant="dangerSoft" icon="Trash2" onClick={() => onDelete(openDoc)}>
                  Eliminar
                </Button>
              </div>
            </div>
          ) : null
        }
      >
        {openDoc && (
          <DocumentDetail
            doc={openDoc}
            employee={empleado}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
            events={events}
            eventsLoading={eventsLoading}
          />
        )}
      </Drawer>

      <UploadDocumentDrawer
        open={showUpload}
        onClose={() => setShowUpload(false)}
        employees={employees}
        categories={categories}
        defaultEmployeeProfileId={employeeProfileId || ""}
        onUploaded={() => {
          setShowUpload(false);
          load();
        }}
      />
    </Card>
  );
}
